import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Alert,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import {
    Store as StoreIcon,
    ChatBubbleOutline as ChatIcon,
    ShoppingBagOutlined as OrderIcon,
    Payment as PaymentIcon,
    LocalShippingOutlined as DeliveryIcon,
    SettingsOutlined as AdvancedIcon,
    LocationOnOutlined as LocationOnOutlinedIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';

import PaymentSettings from './PaymentSettings';
import StoreSettings from './StoreSettings';
import ContactSettings from './ContactSettings';
import StoreAddress from './StoreAddress';
import StoreHours from './StoreHours';
import SecaoPedidos from './SecaoPedidos';
import SecaoEntrega from './SecaoEntrega';
import AdvancedSeoSettings from './AdvancedSeoSettings';

const TABS = [
    { slug: 'general', label: 'Dados da Loja', icon: <StoreIcon /> },
    { slug: 'address', label: 'Endereço', icon: <LocationOnOutlinedIcon /> },
    { slug: 'hours', label: 'Horários', icon: <AccessTimeIcon /> },
    { slug: 'contact', label: 'Contato', icon: <ChatIcon /> },
    { slug: 'orders', label: 'Pedidos', icon: <OrderIcon /> },
    { slug: 'payments', label: 'Pagamentos', icon: <PaymentIcon /> },
    { slug: 'delivery', label: 'Entrega', icon: <DeliveryIcon /> },
    { slug: 'advanced', label: 'Avançado', icon: <AdvancedIcon /> }
];

const Settings = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [hasUnsavedChanges] = useState(false);
    const [data, setData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // Extrai o trecho da URL após "/settings/" (ex: "address")
    const currentSlug = location.pathname.split('/settings/')[1] || 'general';

    // Encontra o índice da aba ativa baseando-se na URL
    const currentTabIndex = TABS.findIndex((tab) => tab.slug === currentSlug);
    // Se a rota for inválida/desconhecida, padrão para a primeira aba (0)
    const selectedTab = currentTabIndex === -1 ? 0 : currentTabIndex;

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/settings`, {
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((data) => {
                setData(data);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Erro ao buscar configurações:", error);
                setIsLoading(false);
            });
    }, []);

    const handleTabChange = (event, newValue) => {
        const targetSlug = TABS[newValue].slug;
        navigate(`/settings/${targetSlug}`);
    };

    return (
        <Box sx={{ p: 0, minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">Configurações</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Gerencie as configurações da sua loja
                    </Typography>
                </Box>
            </Box>

            {/* Unsaved Changes Alert */}
            {hasUnsavedChanges && (
                <Alert severity="warning" sx={{ mb: 3, backgroundColor: '#fff3e0', border: '1px solid #ffe0b2' }}>
                    Você tem alterações não salvas
                </Alert>
            )}

            {/* Tabs Navigation */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    {TABS.map((tab) => (
                        <Tab
                            key={tab.slug}
                            icon={tab.icon}
                            iconPosition="start"
                            label={tab.label}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Main Content Card */}
            <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Routes>
                            <Route index element={<Navigate to="general" replace />} />
                            <Route path="general" element={<StoreSettings data={data} />} />
                            <Route path="address" element={<StoreAddress data={data.address} />} />
                            <Route path="hours" element={<StoreHours data={data.hours} />} />
                            <Route path="contact" element={<ContactSettings data={data.contact} />} />
                            <Route path="orders" element={<SecaoPedidos data={data.orders} />} />
                            <Route path="payments" element={<PaymentSettings data={data.payments} />} />
                            <Route path="delivery" element={<SecaoEntrega data={data.delivery} />} />
                            <Route path="advanced" element={<AdvancedSeoSettings data={data.advanced} />} />
                            <Route path="*" element={<Navigate to="general" replace />} />
                        </Routes>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default Settings;